// src/components/cycles/CycleViewModal.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactPlayer from "react-player";
import Constants from "../Utils/Constants";


// ⚠️ Adapte le chemin si ton fichier est ailleurs
// Exemple possible : "../seances/SeanceDetailsModal"
// ou "../Sessions/SeanceDetailsModal"
import SeanceDetailsModal from "../seances/SeanceDetailsModal";

const { Text, Paragraph } = Typography;

const api = axios.create({
  baseURL: Constants.SERVER_URL,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const WEEKDAY_TO_LABEL = {
  DAY_ONE: "Lun",
  DAY_TWO: "Mar",
  DAY_THREE: "Mer",
  DAY_FOUR: "Jeu",
  DAY_FIVE: "Ven",
  DAY_SIX: "Sam",
  DAY_SEVEN: "Dim",
};

const DAY_ORDER = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const isMinioVideoKey = (value) =>
  typeof value === "string" &&
  (value.startsWith("sessions/") || value.startsWith("videos/"));

const normalizeUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;

  return `${api.defaults.baseURL.replace(/\/$/, "")}/${url.replace(/^\/+/, "")}`;
};

export default function CycleViewModal({ open, cycleKey, onClose }) {
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);

  const [seanceDetailsOpen, setSeanceDetailsOpen] = useState(false);
  const [selectedSeanceKey, setSelectedSeanceKey] = useState(null);

  async function loadCycle() {
    if (!cycleKey) return;

    setLoading(true);
    setError(null);
    setCycle(null);

    try {
      const { data } = await api.get(`/cycle/${encodeURIComponent(cycleKey)}`);
      setCycle(data);
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && cycleKey) {
      loadCycle();
    }
  }, [open, cycleKey]);

  const sessions = useMemo(() => {
    if (!cycle) return [];

    const customSessions = groupSessionDays(
      cycle?.SessionDays || cycle?.sessions || []
    );

    const flexibleSessions = Array.isArray(cycle?.WeeklyCycleFlexibleSessions)
      ? cycle.WeeklyCycleFlexibleSessions.map((item, index) => {
          const session = item?.Session || {};

          return {
            id: `flex-${item.id || index}`,
            sessionId: item?.SessionId,
            sessionKey: session?.key,
            title: session?.name || session?.title || "Séance libre",
            description: session?.description || "",
            videoUrl: session?.videoUrl || "",
            frequency: {
              type: "free",
              amount: item?.amount,
              unit: item?.unit,
            },
          };
        })
      : [];

    return [...customSessions, ...flexibleSessions];
  }, [cycle]);

  async function getSignedVideoUrl(videoUrl) {
    if (!videoUrl) return null;

    if (!isMinioVideoKey(videoUrl)) {
      return normalizeUrl(videoUrl);
    }

    const { data } = await api.get("/api/minio/presign-download", {
      params: {
        bucket: "rxapa-videos",
        key: videoUrl,
      },
    });

    return data?.downloadUrl || null;
  }

  async function openVideoPreview(videoUrl) {
    try {
      const signedUrl = await getSignedVideoUrl(videoUrl);

      if (!signedUrl) {
        Modal.warning({
          title: "Vidéo indisponible",
          content: "Impossible de générer l’aperçu de cette vidéo.",
        });
        return;
      }

      setVideoPreviewUrl(signedUrl);
      setVideoPreviewOpen(true);
    } catch (err) {
      console.error(err);
      Modal.error({
        title: "Erreur vidéo",
        content: "Impossible d’ouvrir l’aperçu vidéo.",
      });
    }
  }

  function openSeanceDetails(sessionKey) {
    if (!sessionKey) {
      Modal.warning({
        title: "Détails indisponibles",
        content:
          "La clé de la séance n’est pas disponible dans la réponse du cycle.",
      });
      return;
    }

    setSelectedSeanceKey(sessionKey);
    setSeanceDetailsOpen(true);
  }

  return (
    <>
      <Modal
        title={
          cycle?.name ? `Résumé du cycle : ${cycle.name}` : "Résumé du cycle"
        }
        open={open}
        onCancel={onClose}
        onOk={onClose}
        okText="Fermer"
        width={980}
        destroyOnClose
      >
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <Spin />
          </div>
        ) : error ? (
          <Alert type="error" message="Erreur lors du chargement du cycle." />
        ) : !cycle ? (
          <Empty description="Aucun cycle trouvé." />
        ) : (
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Nom du cycle">
                {cycle.name || "—"}
              </Descriptions.Item>

              <Descriptions.Item label="Description">
                {cycle.description || "Aucune description"}
              </Descriptions.Item>
            </Descriptions>

            <Space align="center">
              <CalendarOutlined />
              <Typography.Title level={5} style={{ margin: 0 }}>
                Séances associées au cycle
              </Typography.Title>
              <Tag>{sessions.length}</Tag>
            </Space>

            {sessions.length === 0 ? (
              <Empty description="Aucune séance dans ce cycle." />
            ) : (
              <Space direction="vertical" style={{ width: "100%" }} size={12}>
                {sessions.map((session) => (
                  <Card
                    key={session.id}
                    size="small"
                    style={{
                      borderRadius: 12,
                      borderLeft: "4px solid #1677ff",
                    }}
                  >
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      <Space
                        align="start"
                        style={{
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Space direction="vertical" size={4}>
                          <Text strong>{session.title}</Text>

                          <Paragraph
                            type="secondary"
                            ellipsis={{ rows: 2 }}
                            style={{ marginBottom: 0 }}
                          >
                            {session.description || "Aucune description"}
                          </Paragraph>
                        </Space>

                        <FrequencyBadge frequency={session.frequency} />
                      </Space>

                      <Space wrap>
                        {session.videoUrl ? (
                          <Button
                            size="small"
                            icon={<PlayCircleOutlined />}
                            onClick={() => openVideoPreview(session.videoUrl)}
                          >
                            Voir la vidéo
                          </Button>
                        ) : (
                          <Tag icon={<VideoCameraOutlined />}>
                            Aucune vidéo
                          </Tag>
                        )}

                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<EyeOutlined />}
                          onClick={() => openSeanceDetails(session.sessionKey)}
                        >
                          Voir les blocs et exercices
                        </Button>
                      </Space>
                    </Space>
                  </Card>
                ))}
              </Space>
            )}
          </Space>
        )}
      </Modal>

      <Modal
        open={videoPreviewOpen}
        onCancel={() => {
          setVideoPreviewOpen(false);
          setVideoPreviewUrl(null);
        }}
        footer={null}
        title="Aperçu vidéo"
        width={900}
        destroyOnClose
      >
        {videoPreviewUrl ? (
          <div style={{ aspectRatio: "16/9" }}>
            <ReactPlayer
              url={videoPreviewUrl}
              controls
              width="100%"
              height="100%"
            />
          </div>
        ) : (
          <Empty description="Impossible d’afficher la vidéo." />
        )}
      </Modal>

      <SeanceDetailsModal
        open={seanceDetailsOpen}
        seanceKey={selectedSeanceKey}
        onClose={() => {
          setSeanceDetailsOpen(false);
          setSelectedSeanceKey(null);
        }}
      />
    </>
  );
}

function groupSessionDays(sessionDays) {
  const map = new Map();

  sessionDays.forEach((row) => {
    const sessionId = row?.SessionId ?? row?.sessionId ?? row?.id;
    const session = row?.Session || {};

    const sessionKey = session?.key || row?.key || row?.sessionKey;
    const title = session?.name ?? row?.title ?? row?.name ?? "Séance";
    const description = session?.description ?? row?.description ?? "";
    const videoUrl = session?.videoUrl ?? row?.videoUrl ?? "";
    const day = WEEKDAY_TO_LABEL[row?.weekDay] || row?.weekDay;

    if (!sessionId) return;

    if (!map.has(sessionId)) {
      map.set(sessionId, {
        id: `custom-${sessionId}`,
        sessionId,
        sessionKey,
        title,
        description,
        videoUrl,
        days: [],
        frequency: {
          type: "custom",
          days: [],
        },
      });
    }

    if (day) {
      map.get(sessionId).days.push(day);
      map.get(sessionId).frequency.days.push(day);
    }
  });

  return Array.from(map.values()).map((session) => ({
    ...session,
    days: session.days.sort(
      (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
    ),
    frequency: {
      ...session.frequency,
      days: session.frequency.days.sort(
        (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
      ),
    },
  }));
}

function FrequencyBadge({ frequency }) {
  if (!frequency) return <Tag>Fréquence non définie</Tag>;

  if (frequency.type === "free") {
    return (
      <Tag color="purple">
        {frequency.amount || 0}{" "}
        {frequency.unit === "per_day" ? "/ jour" : "/ semaine"}
      </Tag>
    );
  }

  if (frequency.type === "custom") {
    return (
      <Tag color="blue">
        Jours : {(frequency.days || []).join(", ") || "—"}
      </Tag>
    );
  }

  return <Tag>Fréquence non définie</Tag>;
}